import { Locale } from 'app/generated/hygraph';

// Note: Language codes comes from https://academy.useinsider.com/docs/language-codes
export type InsiderLanguageCode =
  | 'en_US'
  | 'zh_cn'
  | 'es_ES'
  | 'ja_JP'
  | 'it_IT'
  | 'pt_PT'
  | 'fr_FR'
  | 'de_DE';

export const InsiderLanguageMap: Record<Locale, InsiderLanguageCode> = {
  [Locale.En]: 'en_US',
  [Locale.De]: 'de_DE',
  [Locale.Es]: 'es_ES',
  [Locale.Fr]: 'fr_FR',
  [Locale.It]: 'it_IT',
  [Locale.Ja]: 'ja_JP',
  [Locale.Pt]: 'pt_PT',
  [Locale.Zh]: 'zh_cn',
};

export type InsiderEventName =
  | 'attraction_page_view'
  | 'offer_page_view'
  | 'home'
  | 'category'
  | 'product';

export type InsiderWebEventName = 'page' | 'product' | 'listing' | 'events';

export type InsiderWebPageName = 'Home' | 'Category' | 'Product' | 'Content';

export interface InsiderProduct {
  productID: string;
  name: string;
  taxonomy: string[];
  imageURL: string;
  price: number;
  currency: string;
  locale: string;
}

export interface InsiderWebProduct {
  id: string;
  name: string;
  taxonomy: string[];
  currency: string;
  unit_price: number;
  unit_sale_price: number;
  url: string;
  product_image_url: string;
}

export interface InsiderWebEvent {
  type: InsiderWebEventName;
  value?: InsiderWebPageName | InsiderWebProduct | Record<string, unknown>;
}

interface InsiderUser {
  userID?: string;
  email?: string;
  locale: Locale;
}

export interface InsiderWebUser {
  uuid?: string;
  email?: string;
  language?: string;
  gdpr_optin?: boolean;
}

export interface InsiderPageParams {
  page_name: string;
  source: string;
  product_id?: string;
  attraction_key?: string;
  image_url?: string;
}

declare global {
  interface Window {
    Insider: {
      track: (name: string, value: Record<string, unknown>[]) => void;
      eventManager: {
        dispatch: (e: string) => unknown;
      };
    };
    InsiderQueue: unknown[];
    insider_object: {
      user: InsiderWebUser;
      page?: unknown;
      product?: unknown;
      listing?: unknown;
    };
  }
}

export interface InsiderServiceConfig {
  init: () => void;

  getUser: () => RNInsiderUserMobile | InsiderWebUser;
  handleModifyUser: (user: InsiderUser) => Promise<void>;
  handleHomePageView: (params: InsiderPageParams) => void;
  handleProductPageView: (product: InsiderProduct, params: InsiderPageParams) => void;
  handleListingPageView: (taxonomy: string[], params: InsiderPageParams) => void;
  handleAttractionPageView: (params: InsiderPageParams) => void;
  handleOffersPageView: (params: InsiderPageParams) => void;
  handleEvent: (
    eventOrName: InsiderEventName | (() => unknown) | InsiderWebEvent,
    params?: (Partial<InsiderPageParams> & { currency?: string }) | Record<string, unknown>
  ) => void;

  startTrackingGeofence?: () => Promise<void>;
  getPermissions?: () => Promise<InsiderPermissions>;
  logout?: () => void;
}

interface InsiderPermissions {
  pushOptIn: boolean;
  locationOptIn: boolean;
}

export interface RNInsiderUserMobile {
  setGender(value: number): this;
  setBirthday(value: Date): this;
  setName(value: string): this;
  setSurname(value: string): this;
  setAge(value: number): this;
  setEmail(value: string): this;
  setPhoneNumber(value: string): this;
  setLanguage(value: string): this;
  setLocale(value: string): this;
  setFacebookID(value: string): this;
  setTwitterID(value: string): this;
  setEmailOptin(value: boolean): this;
  setSMSOptin(value: boolean): this;
  setPushOptin(value: boolean): this;
  setLocationOptin(value: boolean): this;
  setWhatsappOptin(value: boolean): this;

  login(identifiers: Record<string, unknown>, insiderIDResult?: (insiderID: string) => void): this;
  logout(): this;

  setCustomAttributeWithString(key: string, value: string): this;
  setCustomAttributeWithInt(key: string, value: number): this;
  setCustomAttributeWithDouble(key: string, value: number): this;
  setCustomAttributeWithBoolean(key: string, value: boolean): this;
  setCustomAttributeWithDate(key: string, value: Date): this;
  setCustomAttributeWithArray(key: string, value: string[]): this;
  unsetCustomAttribute(key: string): this;
}
